import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import i18n from '@/i18n/i18n';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import SupportRequestSuccessScreen from './SupportRequestSuccessScreen';
import addSupported from '@/assets/icons/add-supported.svg';
import ChooseSupported from '../newSupportRequest/ChooseSupported';
import ChooseSupplier, { SelectedProjectData, SelectedSupplierData } from '../newSupportRequest/ChooseSupplier';
import NewSupportRequestForm from '../newSupportRequest/NewSupportRequestForm';
import NewSupported from '../newSupported/newSupported';
import { useNavigate } from 'react-router-dom';
import useFundContext from '@/contexts/Fund/useFundContext';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { queryKeys } from '@/hooks/QueryKeys';
import { ContactDtoIn } from '@/models/in/ContactDtoIn';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs/Tabs';
import { SupportRequestDraftDtoIn } from '@/models/in/SupportRequestDraftDtoIn';
import { readDraftSupported } from '../newSupportRequest/utils/mapDraftToFormData';
import wizardStyles from './NewSupportRequest.module.scss';
import ReturnedPaymentNewRequestNotice from './ReturnedPaymentSection/ReturnedPaymentNewRequestNotice';

interface NewSupportRequestProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onRefreshList?: () => void;
  /**
   * A request filled in on a client that could not send it - see DraftsStrip. It already
   * names its supported, so the dialog skips its own "who is this for" step and opens
   * straight on the form. Undefined is the ordinary new request, unchanged.
   */
  draft?: SupportRequestDraftDtoIn | null;
  onDraftHandled?: () => void;
  /**
   * HAT-1453: a supported chosen elsewhere (the add-supported success screen on the supporteds
   * screen). Like a draft it skips "who is this for" and opens on the form. The parent clears it
   * through onInitialSupportedHandled when the dialog closes.
   */
  initialSupported?: ContactDtoIn | null;
  onInitialSupportedHandled?: () => void;
}

const NewSupportRequest = ({ open, setOpen, onRefreshList, draft, onDraftHandled, initialSupported, onInitialSupportedHandled }: NewSupportRequestProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // HAT-1105: with the projects feature on, a fund-expense request names a project first.
  const isProjectsEnabled = useFeature('projects');
  /*
   * HAT-1453: the beneficiary is not in the list, so the coordinator adds them without leaving the
   * request. While the add-supported dialog is up this one is hidden (two stacked dialogs would
   * fight over focus), and it comes back - on the choose step, with the list refreshed - when that
   * one closes, or straight onto the request form when the coordinator carries on with the new one.
   */
  const [isAddingSupported, setIsAddingSupported] = useState(false);
  const [isStartSteps, setIsStartSteps] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedSupported, setSelectedSupported] = useState<ContactDtoIn | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<SelectedSupplierData | null>(null);
  const [selectedProject, setSelectedProject] = useState<SelectedProjectData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /*
   * A draft already answered "who is this for", so the choose-a-supported tabs would only
   * ask the coordinator to repeat themselves. Opening straight on the form is also what makes
   * continuing feel like continuing rather than starting again.
   *
   * A draft whose supported cannot be read still opens the form: the rest of its answers are
   * worth more than the one step it skips, and the form asks for what is missing.
   */
  useEffect(() => {
    if (!open || !draft) return;
    setSelectedSupported(readDraftSupported(draft));
    setSelectedSupplier(null);
    setSelectedProject(null);
    setIsStartSteps(true);
  }, [open, draft]);

  useEffect(() => {
    if (!open || !initialSupported || draft) return;
    setSelectedSupported(initialSupported);
    setSelectedSupplier(null);
    setSelectedProject(null);
    setIsStartSteps(true);
  }, [open, initialSupported, draft]);

  const handleStart = (supported: ContactDtoIn) => {
    setSelectedSupported(supported);
    setSelectedSupplier(null);
    setSelectedProject(null);
    setIsStartSteps(true);
  };

  const refreshSupportedsList = () => {
    if (currentFund?.id) queryClient.invalidateQueries({ queryKey: queryKeys.supporteds.basic(currentFund.id) });
  };

  const handleStartWithSupplier = (supplier: SelectedSupplierData, project?: SelectedProjectData | null) => {
    setSelectedSupplier(supplier);
    setSelectedProject(project ?? null);
    setSelectedSupported(null);
    setIsStartSteps(true);
  };

  /*
   * HAT-1105: "במידה ואין לו ספק או פרוייקט הוא יכול להקים אותם דרך שם". There is no project to
   * choose, so the coordinator is taken to where one is opened: the projects list, with the new-project
   * wizard already open (?new=1). The request dialog closes - two stacked dialogs would fight over
   * focus - and the coordinator comes back to it once the project is approved.
   */
  const handleCreateProject = () => {
    setOpen(false);
    setIsStartSteps(false);
    setSelectedProject(null);
    if (currentFund?.id != null) navigate(`/${currentFund.id}/projects/list?new=1`);
  };

  /*
   * ⚠️ Every path that leaves the form clears the draft in the parent as well. Without it
   * the effect above would see the draft still set and re-open it - "add another request"
   * would loop straight back into the one just sent.
   */
  const handleBackToSupportRequests = () => {
    setIsSuccess(false);
    setOpen(false);
    setIsStartSteps(false);
    setSelectedSupported(null);
    setSelectedSupplier(null);
    setSelectedProject(null);
    onDraftHandled?.();
    onInitialSupportedHandled?.();
    if (onRefreshList) {
      onRefreshList();
    }
  };

  const handleAddAnotherRequest = () => {
    setIsSuccess(false);
    setIsStartSteps(false);
    setSelectedSupported(null);
    setSelectedSupplier(null);
    setSelectedProject(null);
    onDraftHandled?.();
    onInitialSupportedHandled?.();
  };

  const handleDialogToggle = (value: boolean) => {
    if (!value && isSubmitting) {
      return;
    }
    if (!value && isSuccess && onRefreshList) {
      onRefreshList();
    }
    setOpen(value);
    setIsAddingSupported(false);
    setIsStartSteps(false);
    setIsSuccess(false);
    setSelectedSupported(null);
    setSelectedSupplier(null);
    setSelectedProject(null);
    if (!value) {
      onDraftHandled?.();
      onInitialSupportedHandled?.();
    }
  };

  return (
    <>
    <MainDialogLayout open={open && !isAddingSupported} onOpenChange={handleDialogToggle} title={t('newSupportRequest')} titleIconSrc={addSupported} preventClose={isSubmitting}>
      {/* עוטף למראה של המסך הצר (ר' NewSupportRequest.module.scss). בדסקטופ הוא שקוף. */}
      <div className={wizardStyles.wizard}>
      {isSuccess ? (
        <SupportRequestSuccessScreen onBackToSupportRequests={handleBackToSupportRequests} onAddAnotherRequest={handleAddAnotherRequest} />
      ) : isStartSteps ? (
        <>
          {/* גלולת "עבור מי" מעל השלבים, כמו .req-chips במקור. מסך צר בלבד. */}
          {(selectedSupported || selectedSupplier) && (
            <div className={wizardStyles.chips}>
              <span className={wizardStyles.chip}>
                {selectedSupported
                  ? `${selectedSupported.firstName ?? ''} ${selectedSupported.lastName ?? ''}`.trim()
                  : [selectedProject?.name, selectedSupplier?.name].filter(Boolean).join(' · ') || t('requestForFund')}
              </span>
            </div>
          )}
          {/* HAT-1395: a support of this supported waits for another bank account - fix it rather than file it again. */}
          {selectedSupported && <ReturnedPaymentNewRequestNotice supportedId={selectedSupported.id} onChanged={() => onRefreshList?.()} />}
        <NewSupportRequestForm
          selectedSupported={selectedSupported}
          selectedSupplier={selectedSupplier}
          selectedProject={selectedProject}
          draft={draft}
          onSuccess={() => setIsSuccess(true)}
          onSubmittingChange={setIsSubmitting}
        />
        </>
      ) : (
        <Tabs
          defaultValue="request-for-supported"
          className={'w-full h-full min-h-0 flex flex-1 flex-col'}
          style={{ direction: i18n.language === 'he' ? 'rtl' : 'ltr' }}
        >
          <TabsList>
            <TabsTrigger value="request-for-supported">{t('requestForSupported')}</TabsTrigger>
            <TabsTrigger value="request-for-fund">{t('requestForFund')}</TabsTrigger>
          </TabsList>
          <TabsContent value="request-for-supported" className="w-full flex-1 min-h-0">
            <ChooseSupported onStart={handleStart} onAddSupported={() => setIsAddingSupported(true)} />
          </TabsContent>
          <TabsContent value="request-for-fund" className="w-full flex-1 min-h-0">
            <ChooseSupplier
              onStart={handleStartWithSupplier}
              projectStep={isProjectsEnabled ? { onCreateProject: handleCreateProject } : undefined}
            />
          </TabsContent>
        </Tabs>
      )}
      </div>
    </MainDialogLayout>
    <NewSupported
      open={open && isAddingSupported}
      setOpen={(isOpen) => setIsAddingSupported(isOpen)}
      onRefreshList={refreshSupportedsList}
      onContinueToRequest={handleStart}
      startedFromRequest
    />
    </>
  );
};

export default NewSupportRequest;
